/**
 * 最小のクライアント側ルーター（History API）。画面は 7 枚なので依存を足さない。
 * 未知のパスも worker が index.html を返す（wrangler.toml の `not_found_handling = "single-page-application"`）。
 */
import { useEffect, useState, type AnchorHTMLAttributes, type MouseEvent } from "react";

export interface Location {
  path: string;
  query: URLSearchParams;
}

function current(): Location {
  return { path: window.location.pathname, query: new URLSearchParams(window.location.search) };
}

const listeners = new Set<() => void>();

export function navigate(to: string, opts: { replace?: boolean } = {}): void {
  if (opts.replace) window.history.replaceState(null, "", to);
  else window.history.pushState(null, "", to);
  window.scrollTo(0, 0);
  for (const l of listeners) l();
}

export function useLocation(): Location {
  const [loc, setLoc] = useState(current);
  useEffect(() => {
    const update = () => setLoc(current());
    listeners.add(update);
    window.addEventListener("popstate", update);
    return () => {
      listeners.delete(update);
      window.removeEventListener("popstate", update);
    };
  }, []);
  return loc;
}

/** `/catalog/:id` のようなパターンに当てる。当たれば params、外れたら null。 */
export function match(pattern: string, path: string): Record<string, string> | null {
  const p = pattern.split("/").filter(Boolean);
  const s = path.split("/").filter(Boolean);
  if (p.length !== s.length) return null;
  const params: Record<string, string> = {};
  for (let i = 0; i < p.length; i++) {
    if (p[i].startsWith(":")) params[p[i].slice(1)] = decodeURIComponent(s[i]);
    else if (p[i] !== s[i]) return null;
  }
  return params;
}

/** 画面内リンク（修飾キー付きクリック・別タブは素のリンクとして通す）。 */
export function Link({ href, onClick, ...rest }: AnchorHTMLAttributes<HTMLAnchorElement> & { href: string }) {
  const handle = (e: MouseEvent<HTMLAnchorElement>) => {
    onClick?.(e);
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    navigate(href);
  };
  return <a href={href} onClick={handle} {...rest} />;
}
